import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Row, Col, Spinner, FormGroup, Form } from "reactstrap";
import Select from 'react-select';
import moment from 'moment';
import Parser from 'html-react-parser';
import * as XLSX from 'xlsx';

// components
import PrintFooter from '../../../components/PrintFooter/PrintFooter';
import { LogotipoBlack, LogoMarca } from "constants/Logotipo";

// api
import grupofde_api from "../../../api/grupofde_api.js";
import fde_api from "../../../api/fde_api.js";
import setup_api from "../../../api/setup_api.js";

// helpers
import { showMessage } from "../../../helpers/message.js";

export default function RelatorioFDE() {
    const [showLoading, setshowLoading] = useState(false);

    const [options_grupos, set_options_grupos] = useState([]);
    const [grupo, set_grupo] = useState(null);
    const [options_competencias, set_options_competencias] = useState([]);
    const [competencia, set_competencia] = useState("");
    const [setup, set_setup] = useState(null);

    const [relatorio, set_relatorio] = useState(null);

    //Colapso das seções expansíveis: parque (por contrato_Id) e memória de cálculo (por contrato_Id+indiceNome)
    const [parquesFechados, set_parquesFechados] = useState({});
    const [memoriasAbertas, set_memoriasAbertas] = useState({});

    //Conversão Recorrência -> meses do período (espelha RecorrenciaHelper.ObterMeses do backend)
    const obterMesesRecorrencia = (recorrencia) => ({
        "Mensal": 1, "Bimestral": 2, "Trimestral": 3, "Quadrimestral": 4, "Semestral": 6, "Anual": 12
    }[recorrencia] || 1);
 
    const gerarCompetencias = (g) => {
        if (!g || !g.proximoProcessamento) return [];
        const meses = obterMesesRecorrencia(g.recorrencia);
        const atual = moment(g.proximoProcessamento).startOf('month').subtract(meses, 'months');
        const opcoes = [];
        for (let i = 0; i < 12; i++) {
            const inicio = atual.clone().subtract(i * meses, 'months');
            const fim = inicio.clone().add(meses - 1, 'months');
            opcoes.push({
                value: inicio.format('YYYY-MM-DD'),
                label: meses > 1 ? `${inicio.format('MM/YYYY')} – ${fim.format('MM/YYYY')}` : inicio.format('MM/YYYY')
            });
        }
        return opcoes;
    }

    const handleChangeGrupo = (g) => {
        set_grupo(g);
        set_relatorio(null);
        const opcoes = gerarCompetencias(g);
        set_options_competencias(opcoes);
        set_competencia(opcoes.length > 0 ? opcoes[0].value : "");
    }

    const loadGrupos = () => {
        grupofde_api.getAll().then(result => {
            if (result.status === 200) {
                set_options_grupos(result.data);
            } else {
                showMessage("Aviso", result.message, 'error', null);
            }
        });
        setup_api.get().then(result => {
            if (result.status === 200) set_setup(result.data);
        });
    }

    useEffect(() => {
        loadGrupos();
    }, []);

    const consultar = () => {
        if (!grupo) {
            showMessage("Aviso", "Selecione o grupo FDE.", 'warning', null);
            return;
        }
        if (!competencia) {
            showMessage("Aviso", "Selecione a competência.", 'warning', null);
            return;
        }

        set_relatorio(null);
        set_parquesFechados({});
        set_memoriasAbertas({});
        setshowLoading(true);
        fde_api.getRelatorio(grupo.grupoFDE_Id, competencia).then(result => {
            setshowLoading(false);
            if (result.status !== 200) {
                showMessage("Aviso", result.message, 'error', null);
                return;
            }
            //Todos os parques iniciam abertos, exceto o primeiro (evita relatório gigante já expandido)
            const fechados = {};
            (result.data.contratos || []).forEach((c, i) => { if (i > 0) fechados[c.contrato_Id] = true; });
            set_parquesFechados(fechados);
            set_relatorio(result.data);
        });
    }

    const toggleParque = (contrato_Id) => {
        set_parquesFechados(prev => ({ ...prev, [contrato_Id]: !prev[contrato_Id] }));
    }

    const toggleMemoria = (chave) => {
        set_memoriasAbertas(prev => ({ ...prev, [chave]: !prev[chave] }));
    }

    //Nomes de índices dinâmicos — união dos índices retornados por todos os contratos
    const nomesIndices = () => {
        if (!relatorio) return [];
        const nomes = new Set();
        relatorio.contratos.forEach(c => (c.indices || []).forEach(i => nomes.add(i.indiceNome)));
        return Array.from(nomes);
    }

    const scoreDoIndice = (contrato, nomeIndice) => {
        const indice = (contrato.indices || []).find(i => i.indiceNome === nomeIndice);
        return indice ? indice.score : null;
    }

    //Formata número com casas fixas (relatório oficial não deve exibir dízima). null/undefined -> "—".
    const fmt = (valor, casas = 2) => (valor === null || valor === undefined) ? "—" : Number(valor).toFixed(casas);
    const fmtPct = (valor) => (valor === null || valor === undefined) ? "—" : `${Number(valor).toFixed(0)}%`;

    //Faixas de Score ID (tabela de conversão Nt -> Score, igual para todos os IDs)
    const corBadgeScore = (score) => {
        if (score === null || score === undefined) return "badge-fde-insuficiente";
        if (score >= 1.0) return "badge-fde-excelente";
        if (score >= 0.9) return "badge-fde-muito-bom";
        if (score >= 0.7) return "badge-fde-bom";
        if (score >= 0.5) return "badge-fde-regular";
        return "badge-fde-insuficiente";
    }

    //Situação do grupo: Oficial só se todo contrato está fechado, Prévia se nenhum, Parcial se misto
    //(cada contrato fecha de forma independente — CE-3489 — então o grupo pode ficar num estado misto)
    const situacaoGrupo = () => {
        if (!relatorio) return null;
        const fechados = relatorio.contratos.filter(c => c.fechado).length;
        if (fechados === 0) return { texto: "Prévia", classe: "badge-warning", icone: "fa-clock" };
        if (fechados === relatorio.contratos.length) return { texto: "Oficial", classe: "badge-success", icone: "fa-lock" };
        return { texto: "Parcial", classe: "badge-info", icone: "fa-adjust" };
    }

    const exportarExcel = () => {
        if (!relatorio) return;
        const indices = nomesIndices();

        const linhas = relatorio.contratos.map(c => {
            const linha = {
                Contrato: c.contratoIdentificacao,
                "Pond. (%)": c.ponderacao,
                NFID: fmt(c.nfid),
            };
            indices.forEach(nome => linha[nome] = fmt(scoreDoIndice(c, nome)));
            linha["NFPS"] = fmt(c.nfps);
            linha["FDEp"] = fmt(c.fdEp);
            return linha;
        });

        linhas.push({
            Contrato: "FDE",
            "Pond. (%)": "",
            NFID: "",
            ...Object.fromEntries(indices.map(n => [n, ""])),
            NFPS: "",
            FDEp: fmt(relatorio.fde)
        });

        const workbook = XLSX.utils.book_new();

        const wsResumo = XLSX.utils.json_to_sheet(linhas);
        XLSX.utils.book_append_sheet(workbook, wsResumo, "Resumo");

        //Detalhamento de índices por contrato (Nt, Score, Peso)
        const linhasIndices = [];
        relatorio.contratos.forEach(c => (c.indices || []).forEach(i => linhasIndices.push({
            Contrato: c.contratoIdentificacao,
            "Índice": i.indiceNome,
            Nt: fmt(i.nt),
            Score: fmt(i.score),
            Peso: fmt(i.peso)
        })));
        if (linhasIndices.length) {
            const wsIndices = XLSX.utils.json_to_sheet(linhasIndices);
            XLSX.utils.book_append_sheet(workbook, wsIndices, "Índices por Contrato");
        }

        //Detalhamento de questões avaliadas por contrato
        const linhasQuestoes = [];
        relatorio.contratos.forEach(c => (c.questoes || []).forEach(q => linhasQuestoes.push({
            Contrato: c.contratoIdentificacao,
            "Índice": q.indiceNome,
            "Questão": q.questaoIdentificacao,
            Resposta: q.valor ?? "",
            Escore: q.escore ?? "Não avaliada"
        })));
        if (linhasQuestoes.length) {
            const wsQuestoes = XLSX.utils.json_to_sheet(linhasQuestoes);
            XLSX.utils.book_append_sheet(workbook, wsQuestoes, "Questões");
        }

        XLSX.writeFile(workbook, `RelatorioFDE_${relatorio.grupoNome}_${competencia}.xlsx`);
    }

    const imprimir = () => {
        //Expande (via estado React) os parques com FDEp antes de imprimir — depender só do
        //CSS de print pra forçar abertura deixava os cards fechados "soltos" no fim da página,
        //fora de ordem, em vez de um embaixo do outro.
        const estadoAnterior = parquesFechados;
        const abertos = {};
        (relatorio.contratos || []).forEach(c => {
            if (c.fdEp != null) abertos[c.contrato_Id] = false;
        });
        set_parquesFechados(prev => ({ ...prev, ...abertos }));

        const restaurarAoFechar = () => {
            set_parquesFechados(estadoAnterior);
            window.removeEventListener('afterprint', restaurarAoFechar);
        };
        window.addEventListener('afterprint', restaurarAoFechar);
        setTimeout(() => window.print(), 50);
    }

    //Badge de situação (Oficial/Prévia) de um contrato, reaproveitado no header do parque
    const badgeSituacaoContrato = (c) => (
        c.fechado
            ? <span className="badge badge-pill badge-success px-2"><i className="fas fa-lock mr-1"></i>Oficial</span>
            : <span className="badge badge-pill badge-warning px-2"><i className="fas fa-clock mr-1"></i>Prévia</span>
    );

    const situacao = situacaoGrupo();

    return (
        <div className="content relatorio-fde">
            {showLoading &&
                <div className="carregandoCmp">
                    <div className="box">
                        <Spinner></Spinner>
                    </div>
                </div>
            }
            <Row className="d-print-none">
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <h4 className="titleCard">Relatório FDE</h4>
                        </CardHeader>
                        <CardBody>
                            <Form>
                                <Row className="align-items-end">
                                    <Col lg="6" md="12" sm="12">
                                        <FormGroup>
                                            <label className="form-control-label">Grupo FDE</label>
                                            <Select
                                                options={options_grupos}
                                                getOptionLabel={o => o.identificacao}
                                                getOptionValue={o => o.grupoFDE_Id}
                                                value={grupo}
                                                onChange={handleChangeGrupo}
                                                classNamePrefix="select"
                                                placeholder="[ Selecione o grupo ]"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="3" md="6" sm="12">
                                        <FormGroup>
                                            <label className="form-control-label">Competência</label>
                                            <Select
                                                options={options_competencias}
                                                value={options_competencias.find(o => o.value === competencia) || null}
                                                onChange={(o) => set_competencia(o.value)}
                                                classNamePrefix="select"
                                                placeholder="[ Selecione ]"
                                                isDisabled={!grupo}
                                                noOptionsMessage={() => "Grupo sem próximo processamento configurado"}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="3" md="6" sm="12">
                                        <Button color="success" type="button" onClick={consultar}>
                                            <i className="fas fa-search"></i>  Consultar
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {relatorio &&
                <Row>
                    <Col md="12">
                        <Card className="shadowFicha">
                            <CardBody>

                                <Row className="mb-2 d-print-none">
                                    <Col md="12" className="text-right">
                                        <Button color="default" size="sm" onClick={exportarExcel}>
                                            <i className="fas fa-file-excel"></i>  Exportar Excel
                                        </Button>
                                        <Button className="ml-2" color="danger" size="sm" onClick={imprimir}>
                                            <i className="fas fa-print"></i>  Imprimir
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Cabeçalho institucional — mesmo padrão de VistoriaFichaImpressao, sempre visível (não só no print) */}
                                {setup &&
                                    <div className="row d-flex justify-content-center align-items-center mb-2">
                                        <div className="col-2 text-center">
                                            <img src={LogotipoBlack()} width="120" alt="Logo"></img>
                                        </div>
                                        <div className="col-8 text-center">
                                            {Parser(setup.cabecalhoDocumentos || "")}
                                        </div>
                                        <div className="col-2 text-center">
                                            <img src={LogoMarca()} width="120" alt="Marca"></img>
                                        </div>
                                    </div>
                                }

                                <h4 className="titleCard font-weight-bold text-center">Relatório de Fator de Desempenho na Execução — FDE</h4>
                                <p className="text-center mb-3" style={{ fontSize: ".8571em" }}>
                                    Grupo FDE: {relatorio.grupoNome} &nbsp;·&nbsp; Competência {(() => {
                                        const meses = obterMesesRecorrencia(grupo && grupo.recorrencia);
                                        const inicio = moment(relatorio.competencia);
                                        return meses > 1
                                            ? `${inicio.format("MM/YYYY")} – ${inicio.clone().add(meses - 1, 'months').format("MM/YYYY")}`
                                            : inicio.format("MM/YYYY");
                                    })()}
                                </p>

                                <h5 className="subtitleCardInfo" style={{ cursor: "default" }}>Resumo do contrato</h5>
                                <Row className="align-items-center p-2">
                                    <Col md="3" className="text-center">
                                        <div style={{ fontSize: ".8571em" }}>FDE da Concessionária</div>
                                        <div className="tab-num" style={{ fontSize: "2.1em" }}>{fmt(relatorio.fde, 3)}</div>
                                        <div className="formula-line text-muted">Σ (FDEp × Ponderação) / Σ Ponderação</div>
                                    </Col>
                                    <Col md="9">
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead><tr><th>Parque</th><th className="text-right">Ponderação</th><th className="text-right">FDEp</th></tr></thead>
                                                <tbody>
                                                    {relatorio.contratos.map(c => (
                                                        <tr key={c.contrato_Id}>
                                                            <td>{c.contratoIdentificacao}</td>
                                                            <td className="text-right tab-num">{fmtPct(c.ponderacao)}</td>
                                                            <td className="text-right tab-num">{fmt(c.fdEp, 3)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Col>
                                </Row>

                                {relatorio.contratos.map(c => {
                                    const colapsado = !!parquesFechados[c.contrato_Id];
                                    return (
                                        <div key={c.contrato_Id}>
                                            <h5 className={`subtitleCardInfo ${colapsado ? "collapsed" : ""}`} onClick={() => toggleParque(c.contrato_Id)}>
                                                <span>
                                                    <i className="fas fa-tree mr-2"></i>
                                                    {c.contratoIdentificacao} — ponderação {fmtPct(c.ponderacao)} — FDEp {fmt(c.fdEp, 3)}
                                                </span>
                                                <span className="d-flex align-items-center">
                                                    <i className="fas fa-chevron-down chev"></i>
                                                </span>
                                            </h5>
                                            <div className={`cardContainer ${colapsado ? "close" : ""} ${c.fdEp == null ? "d-print-none" : ""}`}>
                                                <div className="p-3">
                                                    <div className="memoria-box mb-3">
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-hover mb-0 rel-indicador-table">
                                                                <thead><tr><th>Indicador</th><th className="text-right">Nt (0–10)</th><th className="text-center">Score</th><th className="text-right">Peso</th><th className="text-right">Contribuição</th><th></th></tr></thead>
                                                                <tbody>
                                                                    {(c.indices || []).map(i => {
                                                                        const chave = `${c.contrato_Id}-${i.indiceNome}`;
                                                                        const aberta = !!memoriasAbertas[chave];
                                                                        const contribuicao = (i.score != null && i.peso != null) ? i.score * i.peso : null;
                                                                        const corBadge = corBadgeScore(i.score);
                                                                        return (
                                                                            <React.Fragment key={chave}>
                                                                                <tr className="rel-indicador-row" onClick={() => toggleMemoria(chave)}>
                                                                                    <td>{i.indiceNome}</td>
                                                                                    <td className="text-right tab-num">{fmt(i.nt)}</td>
                                                                                    <td className="text-center"><span className={`badge badge-pill ${corBadge} tab-num`}>{fmt(i.score)}</span></td>
                                                                                    <td className="text-right tab-num">{fmtPct(i.peso * 100)}</td>
                                                                                    <td className="text-right tab-num">{fmt(contribuicao)}</td>
                                                                                    <td className="text-muted"><i className={`fas fa-chevron-${aberta ? "up" : "down"}`}></i></td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td colSpan={6} className="p-0">
                                                                                        <div className={`cardContainer ${aberta ? "" : "close"}`}>
                                                                                            <div className="memoria-box m-2">
                                                                                                <p className="text-muted mb-2" style={{ fontSize: "1.05em", fontWeight: 700 }}>
                                                                                                    Nt apurado por grupo de índices analíticos avaliados em campo: média das inspeções × fator do grupo. A soma dos grupos é o Nt do indicador.
                                                                                                </p>
                                                                                                <table className="table table-sm mb-0">
                                                                                                    <thead><tr><th>Grupo (índice analítico)</th><th className="text-right">Média (0–10)</th><th className="text-right">Fator</th><th className="text-right">Nota do grupo</th></tr></thead>
                                                                                                    <tbody>
                                                                                                        {(i.grupos || []).map((g, gi) => (
                                                                                                            <tr key={gi}>
                                                                                                                <td>{g.indiceAnaliticoNome}</td>
                                                                                                                <td className="text-right tab-num">{fmt(g.media)}</td>
                                                                                                                <td className="text-right tab-num">{fmtPct(g.fator * 100)}</td>
                                                                                                                <td className="text-right tab-num">{fmt(g.notaGrupo)}</td>
                                                                                                            </tr>
                                                                                                        ))}
                                                                                                    </tbody>
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="formula-line mt-2" style={{ fontSize: "1em", fontWeight: 700 }}>
                                                            NFID = {(c.indices && c.indices.length > 0) ? c.indices.map(i => `${fmt(i.score)}×${fmtPct(i.peso * 100)}`).join(" + ") : "—"} = <span className="tab-num" style={{ fontWeight: 700 }}>{fmt(c.nfid)}</span>
                                                        </div>
                                                    </div>

                                                    <div className="memoria-box mb-2 d-flex justify-content-between align-items-center">
                                                        <span style={{ fontSize: "1em", fontWeight: 700 }}>NFPS — pesquisa de satisfação do usuário <span className="badge badge-pill badge-light border" style={{ fontWeight: 400 }}>lançamento manual</span></span>
                                                        <span className="formula-line tab-num" style={{ fontSize: "1em", fontWeight: 700 }}>{fmt(c.nfps)}</span>
                                                    </div>

                                                    <div className="memoria-box">
                                                        <span className="formula-line" style={{ fontSize: "0.9em", fontWeight: 700 }}>FDEp = NFID × 40% + NFPS × 60% = {fmt(c.nfid)} × 0,40 + {fmt(c.nfps)} × 0,60 = {fmt(c.fdEp)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                <PrintFooter />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            }
        </div>
    );
}
